import { ChevronDownIcon, CompassIcon, UsersIcon } from 'lucide-react'
import {
  type CreateOrgType,
  isSubscriptionActive,
  type OrgMembership,
  PERSONA_LABELS,
  roleLabel,
} from '@/shared/domain'
import { useOrgContext } from '@/shared/ui/hooks'
import { OrgBadgeCircle } from '@/shared/ui/components/org-badge-circle'
import { cn } from '@/shared/ui/cn'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/primitives'
import { usePersonaSwitchNavigation } from './use-persona-switch-navigation'

type PersonaEntry = {
  orgId: string
  orgType: CreateOrgType
  name: string
  role: string
  badgeUrl?: string
  subscriptionStatus?: string
}

function toEntry(m: OrgMembership): PersonaEntry {
  return {
    orgId: m.orgId,
    orgType: m.orgType,
    name: m.orgName,
    role: roleLabel(m.role, m.orgType),
    badgeUrl: m.badgeUrl,
    subscriptionStatus: m.subscriptionStatus,
  }
}

function compareByName(a: PersonaEntry, b: PersonaEntry): number {
  return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
}

export function PersonaSwitcher() {
  const ctx = useOrgContext()
  const switchOrg = usePersonaSwitchNavigation()

  const currentMembership = ctx.memberships.find((m) => m.orgId === ctx.orgId) ?? null
  const orgLabel = currentMembership?.orgName ?? PERSONA_LABELS.hunter

  const clubs = ctx.memberships
    .filter((m) => m.orgType === 'club')
    .map(toEntry)
    .sort(compareByName)
  const outfitters = ctx.memberships
    .filter((m) => m.orgType === 'outfitter')
    .map(toEntry)
    .sort(compareByName)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="group flex min-w-0 max-w-full items-center gap-2 bg-transparent text-foreground outline-none transition-colors hover:text-primary focus-visible:outline-none"
        >
          {currentMembership ? (
            <OrgBadgeCircle
              badgeUrl={currentMembership.badgeUrl}
              orgName={currentMembership.orgName}
              isActive
              size={36}
              className="hidden shrink-0 lg:block"
            />
          ) : null}
          <span className="truncate font-display text-base font-bold uppercase tracking-wide lg:text-2xl">
            {orgLabel}
          </span>
          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary lg:size-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-72 max-h-[70vh] overflow-y-auto p-2 lg:w-80"
      >
        {clubs.length > 0 ? (
          <PersonaSection
            icon={UsersIcon}
            label="Clubs"
            entries={clubs}
            selectedOrgId={ctx.orgId}
            onSelect={(id) => switchOrg(id)}
          />
        ) : null}
        {clubs.length > 0 && outfitters.length > 0 ? (
          <DropdownMenuSeparator className="my-2" />
        ) : null}
        {outfitters.length > 0 ? (
          <PersonaSection
            icon={CompassIcon}
            label="Outfitters"
            entries={outfitters}
            selectedOrgId={ctx.orgId}
            onSelect={(id) => switchOrg(id)}
          />
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

type PersonaSectionProps = {
  icon: typeof UsersIcon
  label: string
  entries: PersonaEntry[]
  selectedOrgId: string | null
  onSelect: (orgId: string) => void
}

function PersonaSection({ icon: Icon, label, entries, selectedOrgId, onSelect }: PersonaSectionProps) {
  return (
    <div>
      <DropdownMenuLabel className="flex items-center gap-2 px-2 pb-2 pt-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <Icon aria-hidden="true" className="size-3.5" />
        {label}
      </DropdownMenuLabel>
      <div className="flex flex-col gap-1">
        {entries.map((entry) => {
          const isSelected = entry.orgId === selectedOrgId
          const isActive = isSubscriptionActive(entry.subscriptionStatus)
          return (
            <DropdownMenuItem
              key={entry.orgId}
              onSelect={() => onSelect(entry.orgId)}
              className={cn(
                'flex items-center gap-3 rounded-md px-2 py-2 focus:bg-accent',
                isSelected && 'bg-primary text-primary-foreground focus:bg-primary focus:text-primary-foreground',
              )}
            >
              <OrgBadgeCircle
                badgeUrl={entry.badgeUrl}
                orgName={entry.name}
                isActive={isSelected}
                size={32}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
                <span className="truncate text-sm font-medium">{entry.name}</span>
                <span
                  className={cn(
                    'truncate text-xs',
                    isSelected ? 'text-primary-foreground/80' : 'text-muted-foreground',
                  )}
                >
                  {entry.role}
                </span>
              </div>
              {!isActive ? (
                <span className="shrink-0 rounded-md bg-destructive/15 px-2 py-0.5 text-xs font-medium text-destructive">
                  Inactive
                </span>
              ) : null}
            </DropdownMenuItem>
          )
        })}
      </div>
    </div>
  )
}
